import { useEffect, useRef, useState } from 'react';

import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * @file THE OBJECTIVE PANEL: the tracked mission's name and its one live line,
 * bottom-right, plus a brief banner when a mission starts, is completed or is
 * failed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE LINE, AND IT IS THE TRACKED MISSION'S — NOT THE ACTIVE ONE'S
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine/systems/Missions.js` keeps ACTIVE and TRACKED apart on purpose: one
 * mission runs at a time, but the player may point the HUD at any of the ones
 * they could be doing. So this panel follows `'mission:objective'`, which is
 * the TRACKED mission — including, when nothing is running, a mission that is
 * merely on offer ("Meet the Reds' boss at the hideout"). That is what makes
 * the panel useful before the first mission rather than only during one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT PUBLISHES ON CHANGE, SO IT RE-RENDERS ON CHANGE
 * ─────────────────────────────────────────────────────────────────────────────
 * Unlike `'hud:blips'`, the mission channels fire only when something actually
 * moved — a stage passing, a mission being tracked, a kill changing the count.
 * `useEngineBus` is therefore the right hook here (this IS DOM), and the
 * throttle is nominal.
 *
 * ⚠ THE BANNER IS KEYED, NOT TOGGLED — the same trap `WantedStars.jsx`
 * documents. A CSS animation that has already run sits at its end state
 * forever, so re-setting the class does nothing; the banner node carries a
 * counter in its `key` and React remounts it, replaying the animation from
 * frame zero. Two `mission:event`s in a row therefore both animate.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Seconds a start/complete/fail banner stays up. */
const BANNER_MS = 3200;

export default function MissionPanel({ engine }) {
  const objective = useEngineBus(engine, 'mission:objective', { throttle: 60 });
  const event = useEngineBus(engine, 'mission:event', { throttle: 0 });

  /** @type {[null|{type: string, name: string, critical: boolean}, Function]} */
  const [banner, setBanner] = useState(null);
  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!event?.type) return undefined;
    seqRef.current += 1;
    setBanner(event);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setBanner(null), BANNER_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [event]);

  // A new engine means the old world is gone — and ask it what the objective is
  // rather than waiting for the next change. The HUD mounts before the gameplay
  // install finishes, so the first publish normally lands here anyway; this is
  // what keeps the panel correct after a REMOUNT (a hot reload, a future HUD
  // toggle), when the last change is long past. Same seam the mission list uses.
  useEffect(() => {
    setBanner(null);
    engine?.bus?.emit?.('mission:request');
  }, [engine]);

  const text = objective?.text ?? '';
  const showPanel = !!text;
  if (!showPanel && !banner) return null;

  return (
    <>
      {banner ? <MissionBanner key={seqRef.current} event={banner} /> : null}

      {showPanel ? (
        <div className="pointer-events-none absolute bottom-14 right-3 max-w-[19rem] select-none">
          <div className="rounded-[3px] border border-hairline bg-void/80 px-3.5 py-2.5 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  objective.state === 'active' ? 'bg-gold' : 'bg-hud-faint'
                }`}
                aria-hidden="true"
              />
              <span className="font-display text-[13px] uppercase tracking-[0.16em] text-gold">
                {objective.name}
              </span>
              {objective.stages > 1 && objective.state === 'active' ? (
                <span className="ml-auto font-hud text-[10px] tabular-nums tracking-[0.14em] text-hud-faint">
                  {objective.stage + 1}/{objective.stages}
                </span>
              ) : null}
            </div>
            <p className="mt-1 font-hud text-[13px] leading-snug text-hud-dim">{text}</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * The centre-screen banner. Deliberately NOT in the panel's corner: "MISSION
 * FAILED" is the one piece of mission text the player must not be able to miss,
 * and the corner is exactly where a player mid-firefight is not looking.
 *
 * @param {{event: {type: string, name: string, critical?: boolean, reason?: string}}} props
 */
function MissionBanner({ event }) {
  const kind = event.type;
  const title = kind === 'complete' ? 'Mission passed'
    : kind === 'failed' ? 'Mission failed'
      : 'Mission started';
  const tone = kind === 'complete' ? 'text-gold'
    : kind === 'failed' ? 'text-[#ef6b5e]'
      : 'text-hud';

  return (
    <div className="hud-fade-in pointer-events-none absolute inset-x-0 top-[26%] flex select-none flex-col items-center">
      <div className={`gta-outline font-display text-4xl uppercase leading-none sm:text-5xl ${tone}`}>
        {title}
      </div>
      <div className="mt-2 font-hud text-[12px] uppercase tracking-[0.32em] text-hud-faint">
        {event.name}
        {kind === 'failed' && event.reason ? ` — ${failReason(event.reason)}` : ''}
      </div>
    </div>
  );
}

/**
 * Turns the director's terse `reason` into something a player can act on. The
 * strings are the objective KINDS plus the mission-wide rules, so an unknown
 * one falls through to nothing rather than printing an engine token on screen.
 * @param {string} r
 * @returns {string}
 */
function failReason(r) {
  switch (r) {
    case 'wasted': return 'you died';
    case 'ally': return 'your teammate died';
    case 'time': return 'out of time';
    case 'chase': return 'the target got away';
    case 'protect': return 'you lost them';
    case 'aborted': return 'abandoned';
    default: return '';
  }
}
